<template>
  <v-expansion-panel class="PrepareSignatureSidebarSigner" data-locator="signer-panel">
    <div class="PrepareSignatureSidebarSigner-container mx-4 my-2">
      <v-expansion-panel-title expand-icon="keyboard_arrow_down" data-locator="panel-title">
        <div class="d-flex align-center justify-lg-space-between flex-grow-1 pr-2">
          <div v-if="isWitness" class="d-flex align-center">
            <AppTooltip>
              <template #activator="{ props }">
                <div v-bind="props">
                  <app-material-icon
                    v-if="signerColorToken"
                    name="link"
                    class="mr-2"
                    size="sm"
                    :color="signerColorToken"
                  />
                </div>
              </template>
              <span>{{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSigner.witnessTooltip') }}</span>
            </AppTooltip>
            <span class="text--small text-text-secondary" data-locator="signer-name">{{ computedSignerName }}</span>
          </div>
          <div v-else class="d-flex align-center column-gap-2">
            <div
              v-if="signerColorRgb"
              class="PrepareSignatureSidebarSigner-color"
              :style="{'backgroundColor': signerColorRgb}"
            />
            <AppTextField
              v-if="isEditModeActive"
              v-model.trim="editedSignerName"
              autofocus
              maxlength="100"
              @blur="onRenameSigner"
              @keypress.enter="onRenameSigner"
              @click.stop
            />
            <span v-else
                  :variant="isUniqueSigner ? 'small-bold' : 'small-regular'" class="text-break text-text-secondary"
                  data-locator="signer-name"
            >
            {{ computedSignerName }}
          </span>
            <AppHelpTooltip v-if="isUniqueSigner" :text="uniqueSignerTooltip" />
          </div>

          <div class="d-flex align-center">
            <AppTooltip v-if="isTemplate">
              <template #activator="{ props }">
                <app-button
                  :disabled="isEditModeActive || isReadOnly"
                  :size="$vuetify.display.lgAndDown ? 'small' : 'medium'"
                  type="icon"
                  v-bind="props"
                  @click.stop="onActiveEditMode"
                >
                  <app-material-icon name="edit" />
                </app-button>
              </template>
              <span>{{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSigner.renameSignerLabel') }}</span>
            </AppTooltip>

            <AppTooltip v-if="!isUniqueSigner && !isTemplate">
              <template #activator="{ props }">
                <app-button
                  :disabled="isReadOnly"
                  :size="$vuetify.display.lgAndDown ? 'small' : 'medium'"
                  type="icon"
                  v-bind="props"
                  data-locator="change-signer-button"
                  @click.stop="onChangeSigner"
                >
                  <app-material-icon name="cached" />
                </app-button>
              </template>
              <span> {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSigner.changeSignatory') }} </span>
            </AppTooltip>

            <AppTooltip v-if="!isNdaEnvelope">
              <template #activator="{ props }">
                <app-button
                  :disabled="isDeleteSignerPending || isReadOnly"
                  :loading="isDeleteSignerPending"
                  :size="$vuetify.display.lgAndDown ? 'small' : 'medium'"
                  type="icon"
                  v-bind="props"
                  data-locator="delete-signer-button"
                  @click.stop="onDeleteSigner"
                >
                  <app-material-icon name="delete" />
                </app-button>
              </template>
              <span>
              {{
                  isWitness
                    ? $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSigner.deleteWitness')
                    : $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSigner.deleteSignatory')
                }}
            </span>
            </AppTooltip>
          </div>
        </div>
      </v-expansion-panel-title>
      <v-expansion-panel-text class="pt-2" data-locator="signer-tags">
        <v-row>
          <v-col
            v-for="tag in availableTags"
            :key="`tag-${tag.type}`"
            class="py-1"
            cols="12"
          >
            <PrepareSignatureSidebarSignerTag
              :tag="tag"
              :signer="signer"
              :signer-color="signerColorToken"
              :signer-initials="computedSignerInitials"
              :signer-name="computedSignerName"
              :data-locator="tagToLocatorValue(tag.type)"
              @drag-tag-active="onDragTagActive"
            />
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </div>
  </v-expansion-panel>
</template>

<script>
import { defineComponent } from 'vue'

import AppHelpTooltip from '@/common/AppHelpTooltip.vue'
import AppTextField from '@/common/AppTextField.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { tagsTypes } from '@/common/prepare-signature/tagsTypes'
import { toKebabCase } from '@/common/utils/strings'
import { SignersColors } from '@/project/signing-checklist/prepare-signature/signers-colors'

import PrepareSignatureSidebarSignerTag from './PrepareSignatureSidebarSignerTag.vue'

export default defineComponent({
  name: 'PrepareSignatureSidebarSigner',
  components: {
    AppHelpTooltip,
    AppTextField,
    AppTooltip,
    PrepareSignatureSidebarSignerTag,
  },
  props: {
    deleteSignerPendingIds: {
      type: Array,
      required: true,
    },
    dragActive: {
      type: Boolean,
      required: true,
    },
    isMultipleEnvelope: {
      type: Boolean,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
    },
    isTemplate: {
      type: Boolean,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    order: {
      type: Number,
      required: true,
    },
    provider: {
      type: String,
      required: true,
    },
    signer: {
      type: Object,
      required: true,
    },
    isWitness: {
      type: Boolean,
      default: false,
    },
    isNdaEnvelope: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      editedSignerName: null,
      isEditModeActive: false,
    }
  },
  computed: {
    availableTags () {
      const tags = []
      tags.push({ type: tagsTypes.SIGN, icon: 'signature' })

      if (this.provider !== 'pdf') {
        tags.push(
          { type: tagsTypes.INITIAL, icon: 'id_card'},
          { type: tagsTypes.TEXT, icon: 'text_select_end' },
          { type: tagsTypes.TEXT_READ_ONLY, icon: 'format_size' },
          { type: tagsTypes.NAME, icon: 'id_card' },
          { type: tagsTypes.DATE, icon: 'calendar_today' },
          { type: tagsTypes.CERTIFIED, icon: 'format_quote' },
          { type: tagsTypes.CHECKBOX, icon: 'check_box' },
          { type: tagsTypes.OPTIONAL_CHECKBOX, icon: 'check_box' },
        )
      }
      return tags
    },
    computedSignerName () {
      const numbering = !this.isUniqueSigner ? `#${this.index + 1}` : ''

      if (this.signer.fullName) { return this.signer.fullName }
      if (this.isMultipleEnvelope) {
        if (this.isUniqueSigner) { return this.$t('common.signerType.uniqueSigner', 1) }
        return `${this.$t('common.signerType.commonSigner', 1)} ${numbering}`
      }
      return `${this.$t('common.signer', 1)} ${numbering}`
    },
    computedSignerInitials () {
      if (this.signer.initials) { return this.signer.initials }

      const array = this.computedSignerName.split(' ')
      let string = ''
      array.forEach(i => {
        const initial = i[0]
        string += initial === '#' ? i : i[0]
      })
      return string.toUpperCase()
    },
    isSignerNameEdited () {
      return this.editedSignerName && this.computedSignerName !== this.editedSignerName
    },
    isDeleteSignerPending () {
      return this.deleteSignerPendingIds.includes(this.signer.signatureId)
    },
    isUniqueSigner () {
      return this.signer.signatureType === 'multiple'
    },
    uniqueSignerTooltip () {
      if (this.isNdaEnvelope) {
        return this.$t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSigner.uniqueNdaSignerHelpText')
      }
      return this.$t('project.signing-checklist.prepare-signature.PrepareSignatureSidebarSigner.uniqueSignerHelpText')
    },
    signerColorToken () {
      const i = this.index % (Object.entries(SignersColors).length / 2)
      return SignersColors[i]
    },
    signerColorRgb () {
      return `rgb(var(--v-theme-surface-${this.signerColorToken}))`
    },
  },
  methods: {
    onActiveEditMode () {
      this.editedSignerName = this.computedSignerName
      this.isEditModeActive = true
    },
    onChangeSigner () {
      this.$emit('change-signer', this.signer)
    },
    onDeleteSigner () {
      this.$emit('delete-signer', this.signer.signatureId)
    },
    onDragTagActive (data) {
      this.$emit('drag-tag-active', data)
    },
    onRenameSigner () {
      if (this.isSignerNameEdited) {
        this.$emit('rename-signer', { signatureId: this.signer.signatureId, name: this.editedSignerName })
      }
      this.isEditModeActive = false
    },
    tagToLocatorValue (tagType) {
      return toKebabCase(tagType) + '-tag'
    },
  },
})
</script>

<style scoped lang="scss">
  :deep(.v-expansion-panel-title) {
    padding-inline: 16px;
    padding-block: 12px;
  }
  .PrepareSignatureSidebarSigner {
    transition: none;

    &-container {
      border: 1px solid rgb(var(--v-theme-border-primary));
      border-radius: 8px;
    }

    &-color {
      width: 12px;
      min-width: 12px;
      height: 12px;
      border-radius: 4px;
    }
  }
</style>
